import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    DialogContent,
    MenuItem,
    Divider,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    ListItemText,
    FormLabel,
    FormGroup,
    FormControlLabel,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import DraggableDialog from '../../../ui/draggableDialog';
import { toast } from 'react-toastify';
import { isLocalDev } from '../../../../utils/environment';
import { ValidDataService } from '../../../../service/validDataService';
import { initialValuesSupplierForm, supplierTypes } from './validSupplierInputForm';
import ProvinceSelect from '../../../common/provinceSelect';
import CitySelect from '../../../common/citySelect';
import SectionTitle from '../../../ui/sectionTitle';
import SupplierBrandList from './supplierBrandList';
import SafeDialogContent from 'components/ui/SafeDialogContent';

export default function EditValidSupplierForm({ open, onClose, onRecordEdit, recordToEdit, isCopy = false }) {

    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        filter: '',
        loading: false,
        editIndex: null,
        brand: '',
        type: 'frames',
        isDeleting: false
    });

    useEffect(() => {
        updateState({ loading: true });

    }, [])

    const handleSubmit = async (values, { resetForm }) => {
        try {
            let response;

            if (isCopy) {
                // Remove the id field to avoid duplicate key errors
                const { id, ...newValues } = values;
                // Create a new record
                response = await ValidDataService.addValidSupplier(newValues);
            } else {
                const payload = { ...values, brands: state.data }
                // Update the existing record
                response = await ValidDataService.updateValidSupplierRecord(recordToEdit.id, { ...payload });
            }
            //const response = await ValidDataService.updateValidSupplierRecord(recordToEdit.id, { ...values });
            const { code, data, message } = response;
            if (code === 0) {
                toast.success(message);
                onClose();
                onRecordEdit(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error);
            }
            toast.error('Failed to update record');
        }
    };

    return (
        <DraggableDialog title={isCopy ? "Copy Supplier" : "Edit Supplier"} onClose={onClose} open={open} width={'md'}>
            <SafeDialogContent>
                <Formik
                    initialValues={{ ...initialValuesSupplierForm, ...recordToEdit }}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, isSubmitting, touched, isValid }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} mt={2}>
                                    <Field
                                        as={TextField}
                                        name="supplierName"
                                        label="Supplier Name"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="supplierName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6} mt={2}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        size="small"
                                        fullWidth
                                        select
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12} sm={6} mt={1}>
                                    <FormControl component="fieldset" variant="standard">
                                        <FormLabel component="legend">Supplier Type</FormLabel>
                                        <FormGroup>
                                            <Field
                                                type="checkbox"
                                                name="suppliesFrames"
                                                as={FormControlLabel}
                                                control={<Checkbox />}
                                                label="Frames"
                                            />
                                            <Field
                                                type="checkbox"
                                                name="suppliesLenses"
                                                as={FormControlLabel}
                                                control={<Checkbox />}
                                                label="Lenses"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                    <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                        <FormGroup>
                                            <Field
                                                type="checkbox"
                                                name="suppliesContacts"
                                                as={FormControlLabel}
                                                control={<Checkbox />}
                                                label="Contact Lens"
                                            />
                                            <Field
                                                type="checkbox"
                                                name="suppliesAccessories"
                                                as={FormControlLabel}
                                                control={<Checkbox />}
                                                label="Accessories"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                    {/* <FormControl fullWidth>
                                        <InputLabel>Supplier Type</InputLabel>
                                        <Field
                                            as={Select}
                                            name="supplierType"
                                            label="Supplier type"
                                            size="small"
                                            multiple
                                            renderValue={(selected) => selected.join(', ')}
                                            onChange={(event) => setFieldValue('supplierType', event.target.value)}
                                        >
                                            {supplierTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    <Checkbox checked={values.supplierType.includes(type)} />
                                                    <ListItemText primary={type} />
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </FormControl> */}
                                    <ErrorMessage name="supplierType">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography fontWeight='bold' variant='caption'>Address</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name="addrNo"
                                        label="Number"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrNo">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name="addrStreet"
                                        label="Street"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrStreet">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name="addrUnit"
                                        label="Unit"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrUnit">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        name="addrCity"
                                        component={CitySelect}
                                        countryId={1} // Pass the countryId as a prop
                                        label="City"
                                        size="small"
                                    />
                                    <ErrorMessage name="addrCity">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        name="addrProvince"
                                        component={ProvinceSelect}
                                        countryId={1} // Pass the countryId as a prop
                                        label="Province"
                                        size="small"
                                    />
                                    <ErrorMessage name="addrProvince">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="addrPostal"
                                        label="Postal code"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrPostal">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography fontWeight='bold' variant='caption'>Contact info</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="phone1"
                                        label="Phone Number 1"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="phone1">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="phone2"
                                        label="Phone Number 2"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="phone2">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="email"
                                        label="Email"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="email">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="fax"
                                        label="Fax"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="fax">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="website"
                                        label="Website"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="website">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography fontWeight='bold' variant='caption'>Additional Info</Typography></Divider>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={4}
                                        fullWidth
                                    />
                                    <ErrorMessage name="notes">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                {/* Brands */}
                                <Grid item xs={12}>
                                    <SectionTitle title="Brands" />
                                </Grid>
                                <Grid item xs={12}>

                                </Grid>
                                <Grid item xs={12}>
                                    <SupplierBrandList state={state} updateState={updateState} supplierId={recordToEdit.id} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting || state.isDeleting}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </SafeDialogContent>

        </DraggableDialog>
    );
};